import { BffApiClient } from '@/common/services/api/bffClient';
import { WardApiClient } from '@/common/services/api/ward-backend';
import { profileEndpoints } from '../endpoints/profileEndpoints';

import { logger } from '@/common/services/config';
import type {
  AssistanceModel,
  HobbyModel,
  LanguageModel,
  StudentProfile,
  StudentProfileUpdateData,
  StudentReference,
  StudentSearchFilters,
  StudentSearchQuery,
} from '../types';

export class ProfileService {
  private static get api() {
    return BffApiClient.getInstance();
  }
  private static get wardApi() {
    return WardApiClient.getInstance();
  }

  static async getStudent(id: number | 'current' = 'current'): Promise<StudentProfile | undefined> {
    try {
      const response = await this.api.get<StudentProfile>(profileEndpoints.getStudent(id));
      return response.data;
    } catch (error) {
      logger.error('Error retrieving student :', error);
    }
  }

  static async getCurrentStudentProfile(): Promise<StudentProfile | undefined> {
    try {
      const response = await this.api.get<StudentProfile>(profileEndpoints.getStudentMe);
      return response.data;
    } catch (error) {
      logger.error('Error retrieving student :', error);
    }
  }

  static async getStudentById(id: number): Promise<StudentProfile | undefined> {
    return this.getStudent(id);
  }

  static async updateProfile(data: StudentProfileUpdateData): Promise<StudentProfile> {
    const response = await this.wardApi.put<StudentProfile>(profileEndpoints.updateProfile, data);
    return response.data;
  }

  static async updateStudentPhoto(photo: Blob | null) {
    // Backend expects multipart/form-data with field name 'file'
    const formData = new FormData();
    if (photo) {
      if (photo instanceof File) {
        formData.append('file', photo, photo.name);
      } else {
        formData.append('file', photo, 'photo.jpg');
      }
    }
    await this.api.post(profileEndpoints.updateStudentPhoto, formData);

    // The endpoint returns a string, so refetch the current profile to get updated data
    return await this.getCurrentStudentProfile();
  }

  static async reportStudent(studentId: number, reason: string): Promise<void> {
    await this.api.post(profileEndpoints.reportStudent(studentId), { reason });
  }

  static async searchStudents(query: StudentSearchQuery): Promise<StudentReference[]> {
    const response = await this.api.get<StudentReference[]>(profileEndpoints.searchStudents(query));
    return response.data;
  }

  static async getAssistances(filters?: StudentSearchFilters): Promise<AssistanceModel[]> {
    const response = await this.wardApi.get<AssistanceModel[]>(profileEndpoints.getAssistances);
    return response.data;
  }

  static async addStudentAssistance(assistanceId: number): Promise<void> {
    await this.api.post(profileEndpoints.addStudentAssistance(assistanceId));
  }

  static async removeStudentAssistance(assistanceId: number): Promise<void> {
    await this.api.delete(profileEndpoints.removeStudentAssistance(assistanceId));
  }

  static async getHobbies(filters?: StudentSearchFilters): Promise<HobbyModel[]> {
    const response = await this.wardApi.get<HobbyModel[]>(profileEndpoints.getHobbies);
    return response.data;
  }

  static async addStudentHobby(hobbyId: number): Promise<void> {
    await this.api.post(profileEndpoints.addStudentHobby(hobbyId));
  }

  static async removeStudentHobby(hobbyId: number): Promise<void> {
    await this.api.delete(profileEndpoints.removeStudentHobby(hobbyId));
  }

  static async suggestHobby(label: string): Promise<HobbyModel> {
    const response = await this.api.post<HobbyModel>(profileEndpoints.suggestHobby, { label });
    return response.data;
  }

  static async getLanguages(): Promise<LanguageModel[]> {
    const response = await this.api.get<LanguageModel[]>(profileEndpoints.getLanguages);
    return response.data;
  }

  static async searchCompanies(search?: string, limit?: number): Promise<any[]> {
    const response = await this.api.get<any[]>(profileEndpoints.searchCompanies(search, limit));
    return response.data;
  }

  static async getTopics(): Promise<any[]> {
    const response = await this.api.get<any[]>(profileEndpoints.getTopics);
    return response.data;
  }

  static async getAssociations(): Promise<any[]> {
    const response = await this.api.get<any[]>(profileEndpoints.getAssociations);
    return response.data;
  }

  static async getStudentProfileByMail(mail: string): Promise<StudentProfile | undefined> {
    try {
      const response = await this.api.get<StudentProfile>(
        profileEndpoints.getStudentProfileByMail(mail)
      );
      return response.data;
    } catch (error) {
      logger.error('Error retrieving student profile by mail:', error);
    }
  }
}

export const profileService = new ProfileService();
